import type {
  ICampaignTableAlimentBase,
  ICampaignTableBoxBackgroundBase,
  ICampaignTableBoxPaddingBase,
  ICampaignTableHeading,
} from '../types';
import type { Context, ReactNode } from 'react';

import { createContext, useCallback, useContext, useMemo } from 'react';

export interface CampaignTableProviderProps extends Omit<
  GCampaignTableContextType,
  'parsePaddingRow' | 'getHighlightBgColumn'
> {
  children?: ReactNode;
}

export interface GCampaignTableContextType {
  columnBody: string;
  columnHead: string;
  listHeader: ICampaignTableHeading<string>[];
  alignments: ICampaignTableAlimentBase[];
  parsePaddingRow: (index?: number) => ICampaignTableBoxPaddingBase;
  getHighlightBgColumn: (index: number) => ICampaignTableBoxBackgroundBase;
}

export const GCampaignTableContext: Context<GCampaignTableContextType | undefined> = createContext<
  GCampaignTableContextType | undefined
>(undefined);

export function GCampaignTableProvider({
  children,
  columnBody,
  columnHead,
  listHeader,
  alignments,
}: Readonly<CampaignTableProviderProps>): JSX.Element {
  const parsePaddingRow = useCallback(
    (index?: number) => {
      const padding = index !== undefined ? listHeader[index].padding : {};
      const defaultPaddingTable: ICampaignTableBoxPaddingBase = {
        padding: '200',
      };
      return { ...defaultPaddingTable, ...padding };
    },
    [listHeader],
  );
  const getHighlightBgColumn = useCallback(
    (index: number): ICampaignTableBoxBackgroundBase => {
      const isHighlight = listHeader[index].highlight;
      if (isHighlight) {
        return { background: 'bg-surface-info' };
      }

      return {};
    },
    [listHeader],
  );

  const contextValue = useMemo(
    () => ({
      columnBody,
      columnHead,
      alignments,
      listHeader,
      parsePaddingRow,
      getHighlightBgColumn,
    }),
    [columnBody, columnHead, alignments, listHeader, parsePaddingRow, getHighlightBgColumn],
  );

  return (
    <GCampaignTableContext.Provider value={contextValue}>{children}</GCampaignTableContext.Provider>
  );
}

export const useGCampaignTableContext = () => {
  const context = useContext(GCampaignTableContext);
  if (!context) {
    throw new Error('GCampaignTableContext_IS_NOT_DEFINED');
  }
  return context;
};
